/*!
 * TinyPoker 特殊交收規則管理組件
 * 提供 weekly_special_settlements 的 CRUD 功能
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Button,
  Table,
  Badge,
  Alert,
  Input,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  CustomInput,
} from "reactstrap";
import axios from "axios";
import TinyToggle from "components/tiny/TinyToggle";
import TinyLoading, { TinyBusy, TinyLoadingOverlay } from "components/tiny/TinyLoading";
import { COLORS } from "constant/adminTheme";

const API_BASE = "/api/tiny";

// 規則類型
const RULE_TYPES = [
  { value: "percentage_of_rg_rake", label: "RG 服務費百分比 (NLH+PLO+AOF)" },
  { value: "percentage_of_mtt_fee", label: "MTT FEE 百分比" },
  { value: "percentage_of_biweekly_bonus_pool", label: "雙週獎池百分比 (輸退+活躍)" },
  { value: "fixed_amount", label: "固定金額" },
];

const PERCENTAGE_RULE_TYPES = [
  "percentage_of_rg_rake",
  "percentage_of_mtt_fee",
  "percentage_of_biweekly_bonus_pool",
];

// 雙週獎池規則：純粹兩 club 私事，強制不進 KPI
const BONUS_POOL_RULE_TYPE = "percentage_of_biweekly_bonus_pool";

// 默認規則
const DEFAULT_RULE = {
  settlement_id: "",
  from_club_id: "",
  from_super_agent_id: "",
  to_club_id: "",
  to_club_name: "",
  rule_type: "percentage_of_rg_rake",
  percentage: 0,
  fixed_amount: 0,
  description: "",
  note: "",
  status: "active",
  affects_kpi: false,
  effective_from_week: "",
};

function TinySpecialRules({ embedded = false }) {
  // State
  const [rules, setRules] = useState([]);
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Filter
  const [filterStatus, setFilterStatus] = useState("");

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // create | edit
  const [editingRule, setEditingRule] = useState({ ...DEFAULT_RULE });
  const [saving, setSaving] = useState(false);

  // Delete Confirmation Modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [ruleToDelete, setRuleToDelete] = useState(null);

  // 載入俱樂部列表
  const loadClubs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/clubs`);
      if (res.data.code === 1 && res.data.data?.clubs) {
        setClubs(res.data.data.clubs);
      }
    } catch (err) {
      console.error("載入俱樂部列表失敗:", err);
    }
  }, []);

  // 載入規則列表
  const loadRules = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append("status", filterStatus);

      const res = await axios.get(`${API_BASE}/settlements/special-rules?${params}`);
      if (res.data.code === 1) {
        setRules(res.data.data.rules || []);
      } else {
        setError(res.data.msg);
      }
    } catch (err) {
      setError("載入特殊交收規則失敗: " + (err.response?.data?.msg || err.message));
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  // 初始載入
  useEffect(() => {
    loadClubs();
    loadRules();
  }, [loadClubs, loadRules]);

  // 清除訊息
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  // 取得俱樂部名稱
  const getClubName = (clubId) => {
    const club = clubs.find(c => c.club_id === clubId);
    return club ? club.club_name : clubId;
  };

  // 取得規則類型標籤
  const getRuleTypeLabel = (type) => {
    const found = RULE_TYPES.find(t => t.value === type);
    return found ? found.label : type;
  };

  // 開啟新增 Modal
  const openCreateModal = () => {
    setEditingRule({ ...DEFAULT_RULE });
    setModalMode("create");
    setModalOpen(true);
  };

  // 開啟編輯 Modal
  const openEditModal = (rule) => {
    setEditingRule({ ...rule });
    setModalMode("edit");
    setModalOpen(true);
  };

  // 儲存規則
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const data = { ...editingRule };

      // 根據規則類型清理不需要的欄位
      if (PERCENTAGE_RULE_TYPES.includes(data.rule_type)) {
        delete data.fixed_amount;
      } else {
        delete data.percentage;
      }

      // 雙週獎池規則：強制 affects_kpi=false（兩 club 私事）
      if (data.rule_type === BONUS_POOL_RULE_TYPE) {
        data.affects_kpi = false;
      }

      // effective_from_week 空字串轉 null
      if (!data.effective_from_week) {
        data.effective_from_week = null;
      }
      
      if (modalMode === "create") {
        const res = await axios.post(`${API_BASE}/settlements/special-rules`, data);
        if (res.data.code === 1) {
          setSuccess("新增成功！");
          setModalOpen(false);
          loadRules();
        } else {
          setError(res.data.msg);
        }
      } else {
        const res = await axios.put(`${API_BASE}/settlements/special-rules/${editingRule.settlement_id}`, data);
        if (res.data.code === 1) {
          setSuccess("更新成功！");
          setModalOpen(false);
          loadRules();
        } else {
          setError(res.data.msg);
        }
      }
    } catch (err) {
      setError("儲存失敗: " + (err.response?.data?.msg || err.message));
    } finally {
      setSaving(false);
    }
  };

  // 切換狀態
  const handleToggleStatus = async (rule) => {
    try {
      const res = await axios.patch(`${API_BASE}/settlements/special-rules/${rule.settlement_id}/status`);
      if (res.data.code === 1) {
        setSuccess(`規則已${res.data.data.status === 'active' ? '啟用' : '停用'}！`);
        loadRules();
      } else {
        setError(res.data.msg);
      }
    } catch (err) {
      setError("切換狀態失敗: " + (err.response?.data?.msg || err.message));
    }
  };

  // 開啟刪除確認 Modal
  const openDeleteModal = (rule) => {
    setRuleToDelete(rule);
    setDeleteModalOpen(true);
  };

  // 確認刪除
  const handleDelete = async () => {
    if (!ruleToDelete) return;
    
    try {
      const res = await axios.delete(`${API_BASE}/settlements/special-rules/${ruleToDelete.settlement_id}`);
      if (res.data.code === 1) {
        setSuccess("刪除成功！");
        setDeleteModalOpen(false);
        setRuleToDelete(null);
        loadRules();
      } else {
        setError(res.data.msg);
      }
    } catch (err) {
      setError("刪除失敗: " + (err.response?.data?.msg || err.message));
    }
  };

  // 生成規則 ID
  const generateRuleId = () => {
    const timestamp = Date.now().toString(36);
    return `rule_${timestamp}`;
  };

  return (
    <div className={embedded ? "" : "content"}>
      {/* 提示訊息 */}
      {error && (
        <Alert color="danger" isOpen={!!error} toggle={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert color="success" isOpen={!!success}>
          {success}
        </Alert>
      )}

      {/* 主卡片 */}
      <Card style={{ background: "#1e2530", border: "1px solid rgba(255,255,255,0.1)" }}>
        <CardHeader style={{ background: "#252d3a", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          <Row>
            <Col md={8}>
              <CardTitle tag="h4" style={{ color: "#fff", marginBottom: 0 }}>
                <i className="fa fa-link mr-2" style={{ color: "#11cdef" }} />
                特殊交收規則管理
              </CardTitle>
              <p className="card-category" style={{ color: "rgba(255,255,255,0.6)", marginTop: 6 }}>
                管理俱樂部之間的特殊分潤規則
              </p>
            </Col>
            <Col md={4} className="text-right">
              <Button color="primary" size="sm" onClick={openCreateModal}>
                <i className="fa fa-plus mr-1" />
                新增規則
              </Button>
            </Col>
          </Row>
        </CardHeader>
        <CardBody style={{ padding: "20px" }}>
          {/* 篩選區 */}
          <Row className="mb-3">
            <Col md={3}>
              <FormGroup>
                <Label>狀態篩選</Label>
                <Input
                  type="select"
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="">全部狀態</option>
                  <option value="active">啟用中</option>
                  <option value="inactive">已停用</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>

          {/* 表格 */}
          {loading && rules.length === 0 ? (
            <TinyLoading text="載入中" variant="block" />
          ) : rules.length === 0 ? (
            <div className="text-center p-5">
              <i className="fa fa-inbox fa-3x text-muted mb-3" />
              <p className="text-muted">尚無特殊交收規則</p>
            </div>
          ) : (
            <TinyLoadingOverlay active={loading} text="更新中">
            <Table responsive hover>
              <thead className="text-primary">
                <tr>
                  <th>規則 ID</th>
                  <th>來源</th>
                  <th>目標</th>
                  <th>規則</th>
                  <th>狀態</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {rules.map((rule) => (
                  <tr key={rule.settlement_id} className={rule.status === 'inactive' ? 'text-muted' : ''}>
                    <td>
                      <code>{rule.settlement_id}</code>
                    </td>
                    <td>
                      <div>
                        <strong>{getClubName(rule.from_club_id)}</strong>
                        <small className="text-muted ml-1">({rule.from_club_id})</small>
                      </div>
                      {rule.from_super_agent_id && (
                        <small className="text-info">
                          超代: {rule.from_super_agent_id}
                        </small>
                      )}
                    </td>
                    <td>
                      <div>
                        <strong>{rule.to_club_name || getClubName(rule.to_club_id)}</strong>
                        <small className="text-muted ml-1">({rule.to_club_id})</small>
                      </div>
                    </td>
                    <td>
                      <Badge color="info" className="mr-2">
                        {getRuleTypeLabel(rule.rule_type)}
                      </Badge>
                      {PERCENTAGE_RULE_TYPES.includes(rule.rule_type) ? (
                        <strong>{rule.percentage}%</strong>
                      ) : (
                        <strong>${rule.fixed_amount?.toLocaleString()}</strong>
                      )}
                      {rule.rule_type === BONUS_POOL_RULE_TYPE ? (
                        <Badge color="secondary" className="ml-2">兩 club 私事，不進 KPI</Badge>
                      ) : rule.affects_kpi ? (
                        <Badge color="warning" className="ml-2">進每週額外費用</Badge>
                      ) : (
                        <Badge color="dark" className="ml-2">僅 Excel 顯示</Badge>
                      )}
                      {rule.effective_from_week && (
                        <div>
                          <small className="text-info">生效週: {rule.effective_from_week}</small>
                        </div>
                      )}
                      {rule.description && (
                        <div>
                          <small className="text-muted">{rule.description}</small>
                        </div>
                      )}
                    </td>
                    <td>
                      {/* ⚠️ 原本是 reactstrap `<CustomInput type="switch">` ——
                          Bootstrap 的 custom-switch 在深色主題下（開啟狀態尤其）
                          長得很怪，Brad 2026-09-10 回報。換成公版的 TinyToggle，
                          跟使用者頁的權限開關同一個元件。 */}
                      <div className="d-flex align-items-center" style={{ gap: 8 }}>
                        <TinyToggle
                          on={rule.status === 'active'}
                          color={COLORS.success}
                          onClick={() => handleToggleStatus(rule)}
                          title={`${rule.settlement_id}：${rule.status === 'active' ? '啟用中' : '已停用'}`}
                        />
                        <span style={{
                          fontSize: 12,
                          color: rule.status === 'active' ? COLORS.success : 'rgba(255,255,255,0.45)',
                        }}>
                          {rule.status === 'active' ? '啟用' : '停用'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <Button
                        color="info"
                        size="sm"
                        className="mr-1"
                        onClick={() => openEditModal(rule)}
                      >
                        <i className="fa fa-edit" />
                      </Button>
                      <Button
                        color="danger"
                        size="sm"
                        onClick={() => openDeleteModal(rule)}
                      >
                        <i className="fa fa-trash" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            </TinyLoadingOverlay>
          )}
        </CardBody>
      </Card>

      {/* 新增/編輯 Modal */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
        <ModalHeader toggle={() => setModalOpen(false)}>
          {modalMode === "create" ? "新增特殊交收規則" : "編輯特殊交收規則"}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>規則 ID *</Label>
                <Input
                  type="text"
                  placeholder="如：high_performance_w0616"
                  value={editingRule.settlement_id}
                  onChange={(e) => setEditingRule({ ...editingRule, settlement_id: e.target.value })}
                  disabled={modalMode === "edit"}
                />
                {modalMode === "create" && (
                  <Button
                    color="link"
                    size="sm"
                    className="p-0 mt-1"
                    onClick={() => setEditingRule({ ...editingRule, settlement_id: generateRuleId() })}
                  >
                    自動生成
                  </Button>
                )}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>狀態</Label>
                <Input
                  type="select"
                  value={editingRule.status}
                  onChange={(e) => setEditingRule({ ...editingRule, status: e.target.value })}
                >
                  <option value="active">啟用</option>
                  <option value="inactive">停用</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
          
          <hr />
          <h6>來源設定</h6>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>來源俱樂部 *</Label>
                <Input
                  type="select"
                  value={editingRule.from_club_id}
                  onChange={(e) => setEditingRule({ ...editingRule, from_club_id: e.target.value })}
                >
                  <option value="">選擇俱樂部</option>
                  {clubs.map((club) => (
                    <option key={club.club_id} value={club.club_id}>
                      {club.club_name} ({club.club_id})
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>來源超代 ID（可選）</Label>
                <Input
                  type="text"
                  placeholder="如：1476-4807"
                  value={editingRule.from_super_agent_id || ""}
                  onChange={(e) => setEditingRule({ ...editingRule, from_super_agent_id: e.target.value })}
                />
                <small className="text-muted">不填則從整個俱樂部計算</small>
              </FormGroup>
            </Col>
          </Row>

          <hr />
          <h6>目標設定</h6>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>目標俱樂部 *</Label>
                <Input
                  type="select"
                  value={editingRule.to_club_id}
                  onChange={(e) => {
                    const club = clubs.find(c => c.club_id === e.target.value);
                    setEditingRule({ 
                      ...editingRule, 
                      to_club_id: e.target.value,
                      to_club_name: club ? club.club_name : ""
                    });
                  }}
                >
                  <option value="">選擇俱樂部</option>
                  {clubs.map((club) => (
                    <option key={club.club_id} value={club.club_id}>
                      {club.club_name} ({club.club_id})
                    </option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>目標俱樂部名稱</Label>
                <Input
                  type="text"
                  value={editingRule.to_club_name || ""}
                  onChange={(e) => setEditingRule({ ...editingRule, to_club_name: e.target.value })}
                  placeholder="可自訂顯示名稱"
                />
              </FormGroup>
            </Col>
          </Row>

          <hr />
          <h6>規則設定</h6>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>規則類型 *</Label>
                <Input
                  type="select"
                  value={editingRule.rule_type}
                  onChange={(e) => setEditingRule({ ...editingRule, rule_type: e.target.value })}
                >
                  {RULE_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              {PERCENTAGE_RULE_TYPES.includes(editingRule.rule_type) ? (
                <FormGroup>
                  <Label>百分比 (%)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={editingRule.percentage || 0}
                    onChange={(e) => setEditingRule({ ...editingRule, percentage: parseFloat(e.target.value) || 0 })}
                  />
                </FormGroup>
              ) : (
                <FormGroup>
                  <Label>固定金額</Label>
                  <Input
                    type="number"
                    value={editingRule.fixed_amount || 0}
                    onChange={(e) => setEditingRule({ ...editingRule, fixed_amount: parseFloat(e.target.value) || 0 })}
                  />
                </FormGroup>
              )}
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>進 KPI（自動寫入每週額外費用）</Label>
                <CustomInput
                  type="checkbox"
                  id="affects-kpi-checkbox"
                  checked={
                    editingRule.rule_type === BONUS_POOL_RULE_TYPE
                      ? false
                      : !!editingRule.affects_kpi
                  }
                  disabled={editingRule.rule_type === BONUS_POOL_RULE_TYPE}
                  onChange={(e) => setEditingRule({ ...editingRule, affects_kpi: e.target.checked })}
                  label={
                    editingRule.rule_type === BONUS_POOL_RULE_TYPE
                      ? "雙週獎池規則為兩 club 私事，強制不進 KPI"
                      : "勾選 = 自動算進每週額外費用、影響 final_revenue；不勾 = 只顯示在 Excel"
                  }
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>生效起始週（可選）</Label>
                <Input
                  type="text"
                  placeholder="如：20260518-20260524；空 = 立刻生效"
                  value={editingRule.effective_from_week || ""}
                  onChange={(e) => setEditingRule({ ...editingRule, effective_from_week: e.target.value })}
                />
                <small className="text-muted">該週以前的歷史週 pipeline 不會自動帶入此規則</small>
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label>說明</Label>
            <Input
              type="text"
              placeholder="簡短說明"
              value={editingRule.description || ""}
              onChange={(e) => setEditingRule({ ...editingRule, description: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <Label>備註</Label>
            <Input
              type="textarea"
              rows={2}
              placeholder="詳細備註（可選）"
              value={editingRule.note || ""}
              onChange={(e) => setEditingRule({ ...editingRule, note: e.target.value })}
            />
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setModalOpen(false)}>
            取消
          </Button>
          <Button color="primary" onClick={handleSave} disabled={saving}>
            {saving ? <><TinyBusy light style={{ marginRight: 6 }} />儲存中</> : "儲存"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* 刪除確認 Modal */}
      <Modal isOpen={deleteModalOpen} toggle={() => setDeleteModalOpen(false)}>
        <ModalHeader toggle={() => setDeleteModalOpen(false)}>
          確認刪除
        </ModalHeader>
        <ModalBody>
          {ruleToDelete && (
            <p>
              確定要刪除此特殊交收規則嗎？
              <br />
              <strong>規則 ID：</strong><code>{ruleToDelete.settlement_id}</code>
              <br />
              <strong>來源：</strong>{getClubName(ruleToDelete.from_club_id)}
              <br />
              <strong>目標：</strong>{ruleToDelete.to_club_name || getClubName(ruleToDelete.to_club_id)}
            </p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setDeleteModalOpen(false)}>
            取消
          </Button>
          <Button color="danger" onClick={handleDelete}>
            確認刪除
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default TinySpecialRules;


